import React, { createContext, Suspense, useContext, useEffect, useState } from "react";
import GAnalytic from "react-ga4";
import TagManager from "react-gtm-module";
import { withRouter } from "react-router-dom";

const CookiePolicyDrawer = React.lazy(() => import("@components/CookiePolicyDrawer"));

const isCookieControlFeatureEnabled =
	window.HAS_COOKIE_POLICY_DRAWER || window.HAS_COOKIE_POLICY_DRAWER === "true" || false;

let toggleConsent = () => {};

let getCookie = () => {
	return isCookieControlFeatureEnabled
		? localStorage.getItem("_cookie_consent") === "true"
		: true;
};

let cookieAccepted = () => {
	return localStorage.getItem("_cookie_consent") === "true";
};

let cookieWasSet = () => {
	return cookieAccepted();
};

const CookieContext = createContext({
	cookieStatus: getCookie(),
	setCookieStatus: () => {},
	setShowConsent: toggleConsent,
});

const CookieProvider = withRouter(({ children, location }) => {
	const [showConsent, setShowConsent] = useState(() => {
		const isDocsPath = /^\/docs(\/|$)/.test(window.location.pathname);
		return isCookieControlFeatureEnabled && !isDocsPath && !cookieAccepted();
	});
	const [cookieStatus, setCookieStatus] = useState(() => getCookie());

	useEffect(() => {
		const isDocsPath = /^\/docs(\/|$)/.test(location.pathname);
		const isCookieConsentAccepted = cookieAccepted();

		if (isDocsPath || !isCookieControlFeatureEnabled || isCookieConsentAccepted) {
			setShowConsent(false);
			return;
		}

		setShowConsent(true);
	}, [location.pathname]);

	const handleAccept = () => {
		setCookieStatus(true);
		setShowConsent(false);
		localStorage.setItem("_cookie_consent", true);
	};

	const handleDecline = () => {
		setCookieStatus(false);
		setShowConsent(false);
		localStorage.setItem("_cookie_consent", false);
	};

	useEffect(() => {
		toggleConsent = (consent) => {
			if (!isCookieControlFeatureEnabled) return;
			setShowConsent(consent);
		};
	}, []);

	useEffect(() => {
		if (cookieStatus) {
			GAnalytic.initialize(window.gaConfig);
			TagManager.initialize({
				gtmId: window.gtagConfig,
			});

			if (window.META_PIXEL_ID) {
				ReactPixel.init(
					window.META_PIXEL_ID,
					{},
					{
						autoConfig: true,
						debug: false,
					}
				);
				ReactPixel.pageView();
			}
		}
	}, [cookieStatus]);

	return (
		<CookieContext.Provider value={{ cookieStatus, setCookieStatus, showConsent }}>
			{children}
			<Suspense fallback={null}>
				<CookiePolicyDrawer
					open={showConsent}
					onAccept={handleAccept}
					onDecline={handleDecline}
				/>
			</Suspense>
		</CookieContext.Provider>
	);
});

const useCookie = () => {
	const context = useContext(CookieContext);

	if (!context) {
		throw new Error("useCookie must be used within a CookieProvider");
	}

	return context;
};

export { CookieProvider, cookieWasSet, getCookie, toggleConsent, useCookie };
